import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DropdownOption } from "../../../types/screensTypes";
import { addVoiceToCompany, getVoices, removeVoiceFromCompany } from "../../../api";
import { VoiceAI } from "../../../types/types";
import AudioPlayerRewind from "../../../components/common/AudioPlayer";
import Dropdown from "../../../components/common/Dropdown";
import Icon from "../../../components/common/Icon";
import CloneVoice from "./CloneVoice";
import PromptVoice from "./PromptVoice";
import DeleteModal from "../../../components/modals/DeleteModal";
import { useGlobalState } from "../../../context/useGlobalState";
import { getLanguageDisplayName } from "../../../utils/language";
import styles from "./Voices.module.css";

type VoicesTab = "myVoices" | "library" | "cloneVoice" | "promptVoice";

const Voices: React.FC = () => {
    const [activeTab, setActiveTab] = useState<VoicesTab>("myVoices");
    const [voiceOptions, setVoiceOptions] = useState<VoiceAI[]>([]);
    const [selectedVoice, setSelectedVoice] = useState<VoiceAI | null>(null);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const [selectedLanguage, setSelectedLanguage] = useState<string>("");
    const [selectedType, setSelectedType] = useState<string>("");
    const [isDeleting, setIsDeleting] = useState<Set<string>>(new Set());
    const [deleteModal, setDeleteModal] = useState<{
        isOpen: boolean;
        voice: VoiceAI | null;
    }>({
        isOpen: false,
        voice: null,
    });

    const typeOptions: DropdownOption[] = [
        { value: "male", label: t("male") },
        { value: "female", label: t("female") },
    ];

    const typeLanguage: DropdownOption[] = useMemo(() => {
        if (!data.languagesAI || data.languagesAI.length === 0) {
            return [];
        }
        return data.languagesAI.map((language) => ({
            value: getLanguageDisplayName(language.code),
            label: language.label,
        }));
    }, [data.languagesAI]);

    const fetchVoices = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const voices = await getVoices({ token: token, language: "EN" });
        if (voices) {
            setVoiceOptions(voices);
        } else {
            console.error("Failed to fetch voices");
        }
    };

    const addVoice = async (voiceId: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const addvoice = await addVoiceToCompany({ token, voiceId });
        if (addvoice) {
            await fetchVoices();
        } else {
            console.error("Failed to add voice to company");
        }
    };

    const handleRemoveVoiceClick = (voice: VoiceAI) => {
        if (voice.isCloned) {
            setDeleteModal({
                isOpen: true,
                voice: voice,
            });
        } else {
            removeVoice(voice.id);
        }
    };

    const removeVoice = async (voiceId: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;

        setIsDeleting((prev) => new Set(prev).add(voiceId));

        try {
            const removeVoiceResult = await removeVoiceFromCompany({ token, voiceId });
            if (removeVoiceResult) {
                await fetchVoices();
            } else {
                console.error("Failed to remove voice from company");
            }
        } catch (error) {
            console.error("Error removing voice:", error);
        } finally {
            setIsDeleting((prev) => {
                const newSet = new Set(prev);
                newSet.delete(voiceId);
                return newSet;
            });
        }
    };

    const handleConfirmDelete = () => {
        if (deleteModal.voice) {
            removeVoice(deleteModal.voice.id);
            setDeleteModal({ isOpen: false, voice: null });
        }
    };

    useEffect(() => {
        fetchVoices();
    }, []);

    const handleTabClick = (tab: VoicesTab) => {
        setSelectedVoice(null);
        setActiveTab(tab);
        if (tab === "myVoices" || tab === "library") {
            fetchVoices();
        }
    };

    const showListFilters = activeTab === "myVoices" || activeTab === "library";

    const filteredVoices = useMemo(() => {
        return voiceOptions.filter((voice) => {
            const matchesTab = activeTab === "myVoices" ? voice.isActivated : !voice.isActivated;
            const matchesLanguage =
                !selectedLanguage || voice.language === selectedLanguage || voice.isAllLanguages === true;
            const matchesType = !selectedType || voice.gender === selectedType;
            return matchesTab && matchesLanguage && matchesType;
        });
    }, [voiceOptions, activeTab, selectedLanguage, selectedType]);

    const tabs: { id: VoicesTab; label: string }[] = [
        { id: "myVoices", label: t("myVoices") },
        { id: "library", label: t("library") },
        { id: "cloneVoice", label: t("cloneVoice") },
        { id: "promptVoice", label: t("designVoice") },
    ];

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <h1>{t("voices")}</h1>
                <p>{t("chooseFromOurSelectionOfVoices")}</p>
            </div>

            <div className={styles.tabs} role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`${styles.tab}${activeTab === tab.id ? ` ${styles.tabActive}` : ""}`}
                        onClick={() => handleTabClick(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {showListFilters && (
                <div className={styles.filters}>
                    <Dropdown
                        label={t("language")}
                        options={typeLanguage}
                        onChange={setSelectedLanguage}
                        value={selectedLanguage}
                        placeholder={t("language")}
                        loading={false}
                        error={false}
                        isWithoutMargin
                    />
                    <Dropdown
                        label={t("gender")}
                        options={typeOptions}
                        onChange={setSelectedType}
                        value={selectedType}
                        placeholder={t("gender")}
                        loading={false}
                        error={false}
                        isWithoutMargin
                    />
                </div>
            )}

            {(activeTab === "myVoices" || activeTab === "library") && (
                <div className={styles.list}>
                    {filteredVoices.length === 0 ? (
                        <div className={styles.empty}>{t("noVoicesAvailable") || "Aucune voix"}</div>
                    ) : (
                        filteredVoices.map((voice) => (
                            <div key={voice.id} className={styles.vc}>
                                <button
                                    type="button"
                                    className={styles.play}
                                    disabled={!voice.demoUrl}
                                    title={!voice.demoUrl ? t("clonedVoiceDisabledTooltip") : undefined}
                                    onClick={() => voice.demoUrl && setSelectedVoice(voice)}
                                    aria-label={t("play") || "Play"}
                                >
                                    <Icon name="ic-play" />
                                </button>
                                <div>
                                    <h4 className={styles.vcName}>
                                        {voice.name}
                                        {!voice.demoUrl && activeTab === "library" && (
                                            <span className={styles.vcNameMuted}>({t("clonedVoice")})</span>
                                        )}
                                    </h4>
                                    <p className={styles.vcDesc}>{voice.description}</p>
                                </div>
                                {activeTab === "myVoices" ? (
                                    <button
                                        type="button"
                                        className={styles.btnDel}
                                        onClick={() =>
                                            isDeleting.has(voice.id) ? null : handleRemoveVoiceClick(voice)
                                        }
                                        disabled={isDeleting.has(voice.id)}
                                    >
                                        {t("remove")}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.btnAdd}
                                        onClick={async () => await addVoice(voice.id)}
                                    >
                                        {t("add")}
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === "cloneVoice" && (
                <div className={styles.panel}>
                    <CloneVoice key={voiceOptions.length} />
                </div>
            )}

            {activeTab === "promptVoice" && (
                <div className={styles.panel}>
                    <PromptVoice key={`prompt-${voiceOptions.length}`} />
                </div>
            )}

            {selectedVoice && (
                <div className={styles.player}>
                    <AudioPlayerRewind
                        title={selectedVoice.name}
                        src={selectedVoice.demoUrl}
                        onClickAddToProject={async () => {
                            await addVoice(selectedVoice.id);
                        }}
                        showAddtoProject={false}
                        isAutoPlay={true}
                        hideSeparator
                        embedded
                    />
                </div>
            )}

            <DeleteModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, voice: null })}
                onConfirm={handleConfirmDelete}
                title={t("removeVoice") || t("remove")}
                message={
                    deleteModal.voice
                        ? `${t("confirmRemoveClonedVoice")} "${deleteModal.voice.name}"? ${t("thisActionIsIrreversible")}`
                        : ""
                }
            />
        </div>
    );
};

export default Voices;
